import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { XCircle, CheckCircle2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cancel = () => {
  const { token } = useParams();
  const [confirming, setConfirming] = useState(true);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const doCancel = async () => {
    setBusy(true);
    setErr('');
    try {
      await axios.post(`${API}/appointments/cancel/${token}`);
      setDone(true);
      setConfirming(false);
    } catch (e) {
      setErr(e.response?.data?.detail || 'This cancellation link is invalid or expired.');
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] bg-[#faf6f0] py-16 px-6">
      <div className="max-w-[640px] mx-auto">
        <div className="text-center mb-8">
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-semibold mb-3">CLIENT PORTAL</p>
          <h1 className="font-serif text-4xl text-[#1c1e20]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
            Cancel your appointment
          </h1>
          <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mt-3" />
        </div>

        {confirming && (
          <div className="bg-white border border-black/5 p-10 text-center">
            <div className="w-14 h-14 rounded-full border border-[#c9a961]/60 flex items-center justify-center mx-auto mb-4">
              <AlertTriangle size={24} className="text-[#c9a961]" strokeWidth={1.4} />
            </div>
            <h2 className="font-serif text-2xl text-[#1c1e20] mb-3" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Are you sure you&rsquo;d like to cancel?
            </h2>
            <p className="text-[#4a4a4a] mb-6 text-[15px]">
              This will cancel your Aurelia appointment. You&rsquo;re welcome to book again any time your schedule allows.
            </p>
            {err && <p className="text-rose-600 text-sm mb-3">{err}</p>}
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <button
                onClick={doCancel}
                disabled={busy}
                className="bg-[#141618] hover:bg-black text-white tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5 disabled:opacity-60"
              >
                {busy ? 'CANCELLING…' : 'YES, CANCEL APPOINTMENT'}
              </button>
              <Link
                to="/"
                className="border border-[#1c1e20] hover:bg-[#1c1e20] hover:text-white text-[#1c1e20] tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5 transition-colors"
              >
                KEEP MY APPOINTMENT
              </Link>
            </div>
          </div>
        )}

        {done && (
          <div className="bg-white border border-[#c9a961] p-10 text-center">
            <CheckCircle2 size={36} className="text-[#c9a961] mx-auto mb-3" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl text-[#1c1e20] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
              Your appointment is cancelled.
            </h2>
            <p className="text-[#4a4a4a] mb-6">
              A confirmation email is on its way. We&rsquo;d be honored to serve you again whenever your schedule allows.
            </p>
            <Link to="/contact" className="inline-block bg-[#c9a961] hover:bg-[#b89751] text-[#141618] tracking-[0.22em] text-[11px] font-semibold px-7 py-3 transition-colors">
              BOOK AGAIN
            </Link>
          </div>
        )}

        {!confirming && !done && err && (
          <div className="bg-white border border-black/5 p-10 text-center">
            <XCircle size={32} className="text-rose-500 mx-auto mb-3" />
            <p className="text-[#1c1e20] mb-4">{err}</p>
            <Link to="/contact" className="inline-block bg-[#c9a961] hover:bg-[#b89751] text-[#141618] tracking-[0.22em] text-[11px] font-semibold px-6 py-3 transition-colors">
              CONTACT US
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cancel;
